import React, { useState, useEffect } from 'react';
import InputField from '../components/InputField';
import Button from '../components/Button';
import config from '../config/config';
import { FaInstagram, FaBuilding, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { isExpired, decodeToken } from "react-jwt";
import styled, { keyframes } from 'styled-components';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { API_BASE_URL } = config;

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token !== null && typeof token !== "undefined") {
      if (!isExpired(token)) {
        window.location.href = "/home";
      }
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setResponseMessage('');
    setMessageType('');

    try {
      const response = await fetch(`${API_BASE_URL}/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        const userData = decodeToken(data.token);
        localStorage.setItem("jwtToken", JSON.stringify(data.token).slice(1, -1));
        setResponseMessage(`Bem-vindo, ${userData?.nomeUsuario}!`);
        setMessageType('success');
        // MANAGER escolhe a empresa antes de entrar (com senha provisória, vai para a Home trocar a senha primeiro)
        const destino = userData?.tipoUsuario === 'MANAGER' && !userData?.needUpdatePass ? "/selecionar-empresa" : "/home";
        setTimeout(() => window.location.href = destino, 1500);
      } else if (response.status === 403) {
        const errorText = await response.text();
        setMessageType('warning');
        if (errorText.includes('desativada')) {
          setResponseMessage('Empresa desativada. Entre em contato com o administrador.');
        } else if (errorText.includes('expirado')) {
          setResponseMessage('Período de acesso expirado. Entre em contato com o administrador.');
        } else {
          setResponseMessage('Acesso não autorizado. Entre em contato com o administrador.');
        }
      } else if (response.status === 401) {
        setResponseMessage('Email ou senha incorretos. Tente novamente.');
        setMessageType('error');
      } else if (response.status === 429) {
        setResponseMessage('Muitas tentativas de login. Aguarde alguns minutos e tente novamente.');
        setMessageType('warning');
      } else {
        setResponseMessage('Erro inesperado. Tente novamente mais tarde.');
        setMessageType('error');
      }
    } catch (error) {
      setResponseMessage('Erro de conexão. Verifique sua internet e tente novamente.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const getMessageIcon = () => {
    switch (messageType) {
      case 'error':   return <FaExclamationTriangle style={styles.errorIcon} />;
      case 'warning': return <FaClock style={styles.warningIcon} />;
      case 'success': return <FaBuilding style={styles.successIcon} />;
      default:        return null;
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          {/* ── Logo sem margin negativo ── */}
          <LogoImage src="/logodesp.jpg" alt="Nexus Despachante" />
          <WelcomeText>Bem-vindo ao NEXUS DESPACHANTE</WelcomeText>
        </LogoContainer>

        <LoginForm onSubmit={handleLogin}>
          <InputField
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Email"
            style={styles.input}
            disabled={isLoading}
          />
          <InputField
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha"
            style={styles.input}
            disabled={isLoading}
          />

          <LoginButton type="submit" disabled={isLoading} isLoading={isLoading}>
            {isLoading ? 'Entrando...' : 'Entrar'}
          </LoginButton>

          {responseMessage && (
            <MessageAlert type={messageType}>
              {getMessageIcon()}
              <AlertText type={messageType}>{responseMessage}</AlertText>
            </MessageAlert>
          )}
        </LoginForm>

        <Footer>
          <InstagramLink
            href="https://www.instagram.com/trvisioncgu/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaInstagram style={styles.instagramIcon} />
            <span>Siga nossa página</span>
          </InstagramLink>

          <SupportText>
            Problemas para acessar?{' '}
            <SupportLink href="https://wa.me/5553999457427" target="_blank" rel="noopener noreferrer">
              Contate o suporte
            </SupportLink>
          </SupportText>
        </Footer>
      </LoginCard>
    </LoginContainer>
  );
}

// ── Animações ────────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Styled Components ────────────────────────────────────────────────────────
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  padding: 20px;
  box-sizing: border-box;
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  animation: ${fadeIn} 0.8s ease-out;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 28px 20px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

/* ── CORREÇÃO PRINCIPAL: sem margin negativo, tamanho controlado ── */
const LogoImage = styled.img`
  max-width: 260px;
  width: 100%;
  height: auto;
  display: block;
  margin: 0 auto 16px auto;

  @media (max-width: 480px) {
    max-width: 180px;
  }
`;

const WelcomeText = styled.h1`
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoginButton = styled.button`
  background: ${props => props.isLoading
    ? '#bdc3c7'
    : 'linear-gradient(135deg, #27ae60 0%, #229954 100%)'};
  color: white;
  border: none;
  padding: 15px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(39, 174, 96, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(39, 174, 96, 0.4);
  }

  &:active { transform: translateY(0); }
`;

const MessageAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  border-radius: 12px;
  background: ${props => {
    switch (props.type) {
      case 'error':   return 'rgba(231, 76, 60, 0.1)';
      case 'warning': return 'rgba(243, 156, 18, 0.1)';
      case 'success': return 'rgba(39, 174, 96, 0.1)';
      default:        return 'rgba(52, 152, 219, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'error':   return 'rgba(231, 76, 60, 0.3)';
      case 'warning': return 'rgba(243, 156, 18, 0.3)';
      case 'success': return 'rgba(39, 174, 96, 0.3)';
      default:        return 'rgba(52, 152, 219, 0.3)';
    }
  }};
  animation: ${fadeIn} 0.3s ease-out;
`;

const AlertText = styled.span`
  color: ${props => {
    switch (props.type) {
      case 'error':   return '#c0392b';
      case 'warning': return '#d35400';
      case 'success': return '#27ae60';
      default:        return '#2c3e50';
    }
  }};
  font-weight: 500;
  font-size: 0.95rem;
`;

const Footer = styled.div`
  margin-top: 30px;
  text-align: center;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 20px;
`;

const InstagramLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #7f8c8d;
  font-weight: 500;
  transition: color 0.3s ease;
  &:hover { color: #C13584; }
`;

const SupportText = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-top: 15px;
`;

const SupportLink = styled.a`
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
  &:hover { text-decoration: underline; }
`;

// ── Estilos inline para ícones ───────────────────────────────────────────────
const styles = {
  input: {
    marginBottom: '0',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    padding: '15px',
    fontSize: '16px',
    transition: 'border-color 0.3s ease',
  },
  errorIcon:    { color: '#c0392b', fontSize: '20px', flexShrink: 0 },
  warningIcon:  { color: '#d35400', fontSize: '20px', flexShrink: 0 },
  successIcon:  { color: '#27ae60', fontSize: '20px', flexShrink: 0 },
  instagramIcon:{ fontSize: '20px', color: '#C13584' },
};

export default LoginPage;
